import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  ArrowDown, ArrowUp, Check, Clock3, Copy, Loader2, MapPinned, Pencil, Plus, Route, Sparkles, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { finMesISO, fmtFecha, hoyISO, inicioMesISO } from "@/lib/format";
import { useEmpresa } from "@/hooks/useEmpresa";
import PageHeader from "@/components/PageHeader";
import RutaRoadmapVisual from "@/components/RutaRoadmapVisual";
import MapaSelectorPunto from "@/components/MapaSelectorPunto";
import DireccionEspanaFields from "@/components/DireccionEspanaFields";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { paradasConDesvio } from "@/lib/rutaHorarios";
import ClienteBusquedaSelect from "@/components/ClienteBusquedaSelect";

function formVacioRuta() {
  return {
    id: null,
    usuario_id: "",
    fecha: hoyISO(),
    titulo: "",
    notas: "",
    hora_inicio: "09:00",
    conservar_primera: false,
    origen: null,
    paradas: [],
  };
}

function payloadParadas(paradas) {
  return paradas.map((p, i) => ({
    cliente_id: p.cliente_id || undefined,
    nombre: p.nombre || "",
    direccion: p.direccion || "",
    latitud: p.latitud ?? null,
    longitud: p.longitud ?? null,
    hora_estimada: p.hora_estimada || "",
    hora_visita: p.hora_estimada || "",
    duracion_minutos: Number(p.duracion_minutos) || 30,
    orden: i + 1,
  }));
}

function fmtDesvio(mins) {
  if (mins == null || Number.isNaN(mins)) return null;
  if (Math.abs(mins) < 2) return { txt: "en hora", cls: "text-emerald-700" };
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const cuerpo = h > 0 ? `${h} h ${m} min` : `${m} min`;
  if (mins > 0) return { txt: `+${cuerpo}`, cls: "text-rose-700" };
  return { txt: `−${cuerpo}`, cls: "text-sky-700" };
}

export default function RutasComerciales() {
  const { empresa, cargando: cargandoEmpresa } = useEmpresa();
  const [vista, setVista] = useState("rutas");
  const mesInicio = inicioMesISO();
  const [desde, setDesde] = useState(mesInicio);
  const [hasta, setHasta] = useState(finMesISO(mesInicio));
  const [filtroTrabajador, setFiltroTrabajador] = useState("");
  const [items, setItems] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [trabajadores, setTrabajadores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(formVacioRuta());
  const [clienteForm, setClienteForm] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [duplicarOpen, setDuplicarOpen] = useState(false);
  const [duplicarForm, setDuplicarForm] = useState({ fecha: hoyISO(), usuario_id: "", titulo: "" });
  const [optimizando, setOptimizando] = useState(false);
  const [resumenPlanning, setResumenPlanning] = useState(null);
  const recalcTimer = useRef(null);

  const editando = !!form.id;

  const clientesEnRuta = useMemo(() => {
    const ids = new Set();
    const conteo = new Map();
    for (const p of form.paradas || []) {
      if (p.cliente_id == null) continue;
      const id = String(p.cliente_id);
      ids.add(id);
      conteo.set(id, (conteo.get(id) || 0) + 1);
    }
    return { ids, conteo };
  }, [form.paradas]);

  const paradasDetalle = useMemo(() => paradasConDesvio(detalle?.paradas || []), [detalle]);

  const gruposPorTrabajador = useMemo(() => {
    const map = new Map();
    for (const r of items) {
      const key = String(r.usuario_id);
      if (!map.has(key)) {
        map.set(key, {
          usuario_id: r.usuario_id,
          trabajador: r.trabajador || "Sin asignar",
          rutas: [],
        });
      }
      map.get(key).rutas.push(r);
    }
    const grupos = [...map.values()].map((g) => ({
      ...g,
      rutas: [...g.rutas].sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)) || a.id - b.id),
    }));
    grupos.sort((a, b) => a.trabajador.localeCompare(b.trabajador, "es"));
    return grupos;
  }, [items]);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = { desde, hasta };
      if (filtroTrabajador) params.usuario_id = Number(filtroTrabajador);
      const { data } = await api.get("/rutas-comerciales", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [desde, hasta, filtroTrabajador]);

  const cargarClientes = useCallback(async () => {
    try {
      const { data } = await api.get("/rutas-comerciales/clientes");
      setClientes(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    api.get("/usuarios").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setTrabajadores(list.filter((u) => u.rol !== "super_admin" && u.activo !== false));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (vista === "rutas") cargar();
    if (vista === "clientes") cargarClientes();
  }, [vista, cargar, cargarClientes]);

  const cargarContextoPlanificacion = useCallback(async (usuarioId, fecha, { silencioso = false } = {}) => {
    if (!usuarioId || !fecha) return null;
    try {
      const { data } = await api.get("/rutas-comerciales/contexto-planificacion", {
        params: { usuario_id: Number(usuarioId), fecha },
      });
      setForm((prev) => ({
        ...prev,
        hora_inicio: data.hora_inicio || prev.hora_inicio || "09:00",
        origen: data.origen || null,
      }));
      return data;
    } catch (e) {
      if (!silencioso) toast.error(formatApiError(e));
      return null;
    }
  }, []);

  useEffect(() => {
    if (!formOpen || !form.usuario_id || !form.fecha) return;
    cargarContextoPlanificacion(form.usuario_id, form.fecha, { silencioso: true });
  }, [formOpen, form.usuario_id, form.fecha, cargarContextoPlanificacion]);

  const abrirDetalle = async (id) => {
    try {
      const { data } = await api.get(`/rutas-comerciales/${id}`);
      setDetalle(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirNueva = () => {
    cargarClientes();
    setForm(formVacioRuta());
    setResumenPlanning(null);
    setFormOpen(true);
  };

  const abrirEdicion = async (rutaId) => {
    try {
      await cargarClientes();
      const { data } = await api.get(`/rutas-comerciales/${rutaId}`);
      if (!data.editable_completa) {
        toast.error("Solo se puede editar completamente la ruta antes del día de inicio");
        setDetalle(data);
        return;
      }
      setForm({
        id: data.id,
        usuario_id: String(data.usuario_id),
        fecha: data.fecha,
        titulo: data.titulo || "",
        notas: data.notas || "",
        hora_inicio: (data.paradas?.[0]?.hora_visita || data.paradas?.[0]?.hora_estimada || "09:00"),
        conservar_primera: false,
        origen: null,
        paradas: (data.paradas || []).map((p, i) => ({
          _key: `edit-${p.id || i}`,
          cliente_id: p.cliente_id,
          nombre: p.nombre,
          direccion: p.direccion || "",
          latitud: p.latitud,
          longitud: p.longitud,
          hora_estimada: p.hora_visita || p.hora_estimada || "",
          duracion_minutos: p.duracion_minutos || 30,
          km_desde_anterior: p.km_desde_anterior || null,
          minutos_desplazamiento: p.km_desde_anterior?.minutos || 0,
        })),
      });
      setResumenPlanning(null);
      setDetalle(null);
      setFormOpen(true);
      // Carga origen + horario jornada (ajusta hora_inicio a la salida de empresa)
      try {
        const { data: ctx } = await api.get("/rutas-comerciales/contexto-planificacion", {
          params: { usuario_id: data.usuario_id, fecha: data.fecha },
        });
        setForm((prev) => ({
          ...prev,
          hora_inicio: ctx.hora_inicio || prev.hora_inicio,
          origen: ctx.origen || null,
        }));
      } catch {
        /* ignore */
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const anadirParadaForm = (clienteId) => {
    const c = clientes.find((x) => String(x.id) === String(clienteId));
    if (!c) return;
    setForm((prev) => ({
      ...prev,
      paradas: [
        ...prev.paradas,
        {
          _key: `${c.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          cliente_id: c.id,
          nombre: c.nombre,
          direccion: [c.direccion, c.codigo_postal, c.poblacion, c.provincia].filter(Boolean).join(", "),
          latitud: c.latitud,
          longitud: c.longitud,
          hora_estimada: "",
          duracion_minutos: 30,
          km_desde_anterior: null,
          minutos_desplazamiento: 0,
        },
      ],
    }));
    setResumenPlanning(null);
  };

  const actualizarParada = (key, patch) => {
    setForm((prev) => ({
      ...prev,
      paradas: prev.paradas.map((x) => (x._key === key ? { ...x, ...patch } : x)),
    }));
  };

  const moverParada = (index, dir) => {
    setForm((prev) => {
      const next = [...prev.paradas];
      const j = index + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[index], next[j]] = [next[j], next[index]];
      return { ...prev, paradas: next };
    });
    setResumenPlanning(null);
  };

  const quitarParada = (key) => {
    setForm((prev) => ({
      ...prev,
      paradas: prev.paradas.filter((x) => x._key !== key),
    }));
    setResumenPlanning(null);
  };

  const aplicarResultadoPlanning = (data, prevParadas = null) => {
    const base = prevParadas || form.paradas;
    setForm((prev) => ({
      ...prev,
      hora_inicio: data.hora_inicio || prev.hora_inicio,
      origen: data.origen !== undefined ? data.origen : prev.origen,
      paradas: (data.paradas || []).map((p, i) => ({
        _key: base[i]?._key || prev.paradas[i]?._key || `opt-${i}-${Date.now()}`,
        cliente_id: p.cliente_id,
        nombre: p.nombre,
        direccion: p.direccion || "",
        latitud: p.latitud,
        longitud: p.longitud,
        hora_estimada: p.hora_estimada || p.hora_visita || "",
        duracion_minutos: p.duracion_minutos || 30,
        km_desde_anterior: p.km_desde_anterior || null,
        minutos_desplazamiento: p.minutos_desplazamiento || p.km_desde_anterior?.minutos || 0,
      })),
    }));
    setResumenPlanning({
      km_total: data.km_total,
      minutos_viaje_total: data.minutos_viaje_total,
      hora_fin_estimada: data.hora_fin_estimada,
      origen: data.origen,
    });
  };

  const bodyPlanning = (paradas, extra = {}) => ({
    paradas: payloadParadas(paradas),
    hora_inicio: form.hora_inicio || undefined,
    usuario_id: form.usuario_id ? Number(form.usuario_id) : undefined,
    fecha: form.fecha || undefined,
    ...extra,
  });

  const optimizarRuta = async () => {
    if (form.paradas.length < 2) return toast.error("Añade al menos dos paradas");
    if (!form.usuario_id) return toast.error("Selecciona un trabajador para usar su horario y origen");
    setOptimizando(true);
    try {
      const { data } = await api.post("/rutas-comerciales/optimizar", bodyPlanning(form.paradas, {
        conservar_primera: !!form.conservar_primera,
        planificar_horarios: true,
      }));
      const prevByCliente = new Map(form.paradas.map((p) => [`${p.cliente_id}-${p.nombre}`, p._key]));
      setForm((prev) => ({
        ...prev,
        hora_inicio: data.hora_inicio || prev.hora_inicio,
        origen: data.origen !== undefined ? data.origen : prev.origen,
        paradas: (data.paradas || []).map((p, i) => ({
          _key: prevByCliente.get(`${p.cliente_id}-${p.nombre}`) || `opt-${i}-${Date.now()}`,
          cliente_id: p.cliente_id,
          nombre: p.nombre,
          direccion: p.direccion || "",
          latitud: p.latitud,
          longitud: p.longitud,
          hora_estimada: p.hora_estimada || p.hora_visita || "",
          duracion_minutos: p.duracion_minutos || 30,
          km_desde_anterior: p.km_desde_anterior || null,
          minutos_desplazamiento: p.minutos_desplazamiento || p.km_desde_anterior?.minutos || 0,
        })),
      }));
      setResumenPlanning({
        km_total: data.km_total,
        minutos_viaje_total: data.minutos_viaje_total,
        hora_fin_estimada: data.hora_fin_estimada,
        origen: data.origen,
      });
      const origenTxt = data.origen?.nombre ? ` desde ${data.origen.nombre}` : "";
      toast.success(`Ruta optimizada${origenTxt} · ${Number(data.km_total || 0).toFixed(1)} km · fin ~ ${data.hora_fin_estimada || "—"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOptimizando(false);
    }
  };

  const planificarHorarios = async (paradasOverride = null, anclarDesde = null, horaInicioOverride = null) => {
    const paradas = paradasOverride || form.paradas;
    if (!paradas.length) {
      if (anclarDesde == null && !horaInicioOverride) toast.error("Añade paradas primero");
      return;
    }
    if (!form.usuario_id) {
      if (anclarDesde == null && !horaInicioOverride) toast.error("Selecciona un trabajador");
      return;
    }
    setOptimizando(true);
    try {
      const { data } = await api.post(
        "/rutas-comerciales/planificar-horarios",
        bodyPlanning(paradas, {
          anclar_desde_indice: anclarDesde == null ? undefined : anclarDesde,
          ...(horaInicioOverride ? { hora_inicio: horaInicioOverride } : {}),
        }),
      );
      aplicarResultadoPlanning(data, paradas);
      if (anclarDesde == null && !horaInicioOverride) {
        toast.success(`Horarios planificados (salida ${data.hora_inicio || "—"}) · fin ${data.hora_fin_estimada || "—"}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOptimizando(false);
    }
  };

  const onCambiarHoraVisita = (key, hora) => {
    const idx = form.paradas.findIndex((x) => x._key === key);
    const next = form.paradas.map((x) => (x._key === key ? { ...x, hora_estimada: hora } : x));
    setForm((prev) => ({
      ...prev,
      paradas: next,
    }));
    if (idx < 0 || !hora || !form.usuario_id) return;
    if (recalcTimer.current) clearTimeout(recalcTimer.current);
    recalcTimer.current = setTimeout(() => {
      planificarHorarios(next, idx);
    }, 450);
  };

  const onCambiarDuracion = (key, duracion) => {
    const idx = form.paradas.findIndex((x) => x._key === key);
    const next = form.paradas.map((x) => (x._key === key ? { ...x, duracion_minutos: duracion } : x));
    setForm((prev) => ({ ...prev, paradas: next }));
    if (idx < 0 || !form.usuario_id) return;
    if (recalcTimer.current) clearTimeout(recalcTimer.current);
    recalcTimer.current = setTimeout(() => {
      planificarHorarios(next, idx);
    }, 450);
  };

  const guardarRuta = async () => {
    if (!form.usuario_id) return toast.error("Selecciona un trabajador");
    if (!form.paradas.length) return toast.error("Añade al menos una parada");
    setGuardando(true);
    try {
      const paradas = payloadParadas(form.paradas);
      let data;
      if (editando) {
        ({ data } = await api.put(`/rutas-comerciales/${form.id}`, {
          fecha: form.fecha,
          titulo: form.titulo,
          notas: form.notas,
          paradas,
        }));
        toast.success("Ruta actualizada. El trabajador ha sido notificado.");
      } else {
        ({ data } = await api.post("/rutas-comerciales", {
          usuario_id: Number(form.usuario_id),
          fecha: form.fecha,
          titulo: form.titulo,
          notas: form.notas,
          paradas,
        }));
        toast.success("Ruta creada. El trabajador ha sido notificado.");
      }
      setFormOpen(false);
      setForm(formVacioRuta());
      setResumenPlanning(null);
      if (form.fecha < desde || form.fecha > hasta) {
        setDesde(`${form.fecha.slice(0, 8)}01`);
        setHasta(finMesISO(form.fecha));
      }
      setDetalle(data);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const guardarCliente = async () => {
    if (!clienteForm?.nombre?.trim()) return toast.error("Nombre obligatorio");
    setGuardando(true);
    try {
      const payload = { ...clienteForm, geocodificar: true, activo: true };
      if (clienteForm.id) await api.put(`/rutas-comerciales/clientes/${clienteForm.id}`, payload);
      else await api.post("/rutas-comerciales/clientes", payload);
      toast.success("Cliente guardado");
      setClienteForm(null);
      cargarClientes();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const cancelarRuta = async (id) => {
    if (!window.confirm("¿Cancelar esta ruta? Se aplicará de inmediato y solo se notificará al trabajador (sin su confirmación).")) return;
    try {
      await api.delete(`/rutas-comerciales/${id}`);
      toast.success("Ruta cancelada. Trabajador notificado (sin confirmación).");
      setDetalle(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirDuplicar = () => {
    if (!detalle) return;
    setDuplicarForm({
      fecha: hoyISO(),
      usuario_id: String(detalle.usuario_id || ""),
      titulo: detalle.titulo ? `${detalle.titulo} (copia)` : "",
    });
    setDuplicarOpen(true);
  };

  const duplicarRuta = async () => {
    if (!detalle) return;
    if (!duplicarForm.fecha) return toast.error("Indica la nueva fecha");
    setGuardando(true);
    try {
      const payload = {
        fecha: duplicarForm.fecha,
        titulo: duplicarForm.titulo || undefined,
      };
      if (duplicarForm.usuario_id) payload.usuario_id = Number(duplicarForm.usuario_id);
      const { data } = await api.post(`/rutas-comerciales/${detalle.id}/duplicar`, payload);
      toast.success("Ruta duplicada. Puedes editarla en la nueva fecha.");
      setDuplicarOpen(false);
      setDesde(`${duplicarForm.fecha.slice(0, 8)}01`);
      setHasta(finMesISO(duplicarForm.fecha));
      setDetalle(data);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const actualizarHoraDetalle = async (paradaId, hora) => {
    try {
      await api.put(`/rutas-comerciales/paradas/${paradaId}`, { hora_visita: hora });
      await abrirDetalle(detalle.id);
      toast.success("Hora de visita actualizada");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const irMesActual = () => {
    const ini = inicioMesISO();
    setDesde(ini);
    setHasta(finMesISO(ini));
  };

  if (!cargandoEmpresa && empresa && !empresa.gestionar_rutas_comerciales) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Route}
        title="Rutas comerciales"
        subtitle="Planifica visitas por fecha con vista roadmap, mapas y seguimiento"
        sectionKey="rutas"
        brandGradient={false}
      >
        <Button className="gap-1.5 bg-white/20 text-white hover:bg-white/30" onClick={abrirNueva}>
          <Plus className="h-4 w-4" /> Nueva ruta
        </Button>
      </PageHeader>

      <Tabs value={vista} onValueChange={setVista}>
        <TabsList>
          <TabsTrigger value="rutas">Planning</TabsTrigger>
          <TabsTrigger value="clientes">Clientes / direcciones</TabsTrigger>
        </TabsList>
      </Tabs>

      {vista === "clientes" ? (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setClienteForm({
              nombre: "", codigo: "", nif: "", email: "", telefono: "",
              direccion: "", poblacion: "", provincia: "", codigo_postal: "",
              latitud: null, longitud: null, notas: "",
            })}>
              <Plus className="mr-1 h-4 w-4" /> Nuevo cliente
            </Button>
          </div>
          <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
            {clientes.length === 0 ? (
              <p className="p-10 text-center text-sm text-zinc-500">No hay clientes. Crea el maestro con dirección para geolocalizar visitas.</p>
            ) : clientes.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-100 p-4 last:border-0">
                <div>
                  <p className="font-medium text-zinc-900">{c.nombre}</p>
                  <p className="text-xs text-zinc-500">
                    {[c.direccion, c.poblacion, c.provincia].filter(Boolean).join(" · ") || "Sin dirección"}
                  </p>
                  {c.maps_url && (
                    <a href={c.maps_url} target="_blank" rel="noreferrer" className="text-[11px] text-sky-600 hover:underline">
                      Ver en Google Maps
                    </a>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={() => setClienteForm({ ...c })}>Editar</Button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-zinc-200 bg-white p-3">
            <div>
              <Label>Desde</Label>
              <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" />
            </div>
            <div className="min-w-[12rem]">
              <Label>Trabajador</Label>
              <Select value={filtroTrabajador || "all"} onValueChange={(v) => setFiltroTrabajador(v === "all" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los trabajadores</SelectItem>
                  {trabajadores.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>{u.nombre} {u.apellidos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={irMesActual}>Mes actual</Button>
            <Button variant="outline" onClick={cargar}>Actualizar</Button>
          </div>

          {cargando ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-rose-500" /></div>
          ) : gruposPorTrabajador.length === 0 ? (
            <p className="rounded-xl border border-dashed border-zinc-200 py-16 text-center text-sm text-zinc-500">
              No hay rutas entre {fmtFecha(desde)} y {fmtFecha(hasta)}.
            </p>
          ) : (
            <div className="space-y-4">
              {gruposPorTrabajador.map((g) => (
                <section key={g.usuario_id} className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
                  <header className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 bg-zinc-50 px-4 py-3">
                    <div>
                      <h3 className="font-semibold text-zinc-900">{g.trabajador}</h3>
                      <p className="text-xs text-zinc-500">{g.rutas.length} ruta{g.rutas.length === 1 ? "" : "s"} en el periodo</p>
                    </div>
                  </header>
                  <ul className="divide-y divide-zinc-100">
                    {g.rutas.map((r) => (
                      <li
                        key={r.id}
                        className={`flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-zinc-50/80 ${
                          r.estado === "cancelada" ? "opacity-70" : ""
                        }`}
                      >
                        <button type="button" className="min-w-0 flex-1 text-left" onClick={() => abrirDetalle(r.id)}>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline">{fmtFecha(r.fecha)}</Badge>
                            <span className={`font-medium ${r.estado === "cancelada" ? "text-zinc-500 line-through" : "text-zinc-900"}`}>
                              {r.titulo || "Sin título"}
                            </span>
                            <Badge
                              className={
                                r.estado === "cancelada"
                                  ? "bg-rose-50 text-rose-700 hover:bg-rose-50"
                                  : "bg-zinc-100 text-zinc-700 hover:bg-zinc-100"
                              }
                            >
                              {r.estado}
                            </Badge>
                          </div>
                          <p className="mt-1 text-xs text-zinc-500">
                            {r.num_paradas} paradas · {r.paradas_visitadas} visitadas
                            {r.km_total > 0 ? ` · ${Number(r.km_total).toFixed(1)} km` : ""}
                          </p>
                        </button>
                        <div className="flex gap-2">
                          {r.editable_completa && (
                            <Button size="sm" variant="outline" onClick={() => abrirEdicion(r.id)}>
                              <Pencil className="mr-1 h-3.5 w-3.5" /> Editar
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => abrirDetalle(r.id)}>Ver</Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}

          {detalle && (
            <div className="space-y-4 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-zinc-900">{detalle.titulo}</h2>
                  <p className="text-sm text-zinc-500">
                    {detalle.trabajador} · {fmtFecha(detalle.fecha)} · {detalle.estado}
                    {detalle.km_total > 0 ? ` · ${Number(detalle.km_total).toFixed(1)} km` : ""}
                  </p>
                  {detalle.origen && (
                    <p className="mt-1 text-xs text-violet-800/80">
                      Origen km: {detalle.origen.nombre || "Empresa"}
                      {detalle.origen.fuente === "primer_picado"
                        ? " (GPS del primer picado)"
                        : " (centro de trabajo)"}
                    </p>
                  )}
                  {detalle.editable_completa && (
                    <p className="mt-1 text-xs text-emerald-700">Editable hasta el día anterior a la ruta.</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {detalle.editable_completa && (
                    <Button variant="outline" onClick={() => abrirEdicion(detalle.id)}>
                      <Pencil className="mr-1 h-4 w-4" /> Editar
                    </Button>
                  )}
                  <Button variant="outline" onClick={abrirDuplicar}>
                    <Copy className="mr-1 h-4 w-4" /> Duplicar
                  </Button>
                  <Button variant="outline" className="text-rose-600" onClick={() => cancelarRuta(detalle.id)}>
                    <Trash2 className="mr-1 h-4 w-4" /> Cancelar
                  </Button>
                  <Button variant="outline" onClick={() => setDetalle(null)}>Cerrar</Button>
                </div>
              </div>

              {(detalle.paradas || []).length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-zinc-100">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-100 text-left text-[11px] uppercase tracking-wide text-zinc-500">
                        <th className="px-3 py-2">#</th>
                        <th className="px-3 py-2">Cliente</th>
                        <th className="px-3 py-2">Estado</th>
                        <th className="px-3 py-2">Hora planificada</th>
                        <th className="px-3 py-2">Hora recálc.</th>
                        <th className="px-3 py-2">Desvío</th>
                        <th className="px-3 py-2">Duración</th>
                        <th className="px-3 py-2">Distancia desde anterior</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paradasDetalle.map((p) => {
                        const desvio = fmtDesvio(p.desvio_minutos);
                        return (
                        <tr
                          key={p.id}
                          className={`border-b border-zinc-50 last:border-0 ${p.realizada ? "bg-emerald-50/50" : ""}`}
                        >
                          <td className="px-3 py-2 font-medium">{String(p.orden).padStart(2, "0")}</td>
                          <td className="px-3 py-2">
                            <p className="font-medium text-zinc-900">{p.nombre}</p>
                            <p className="text-xs text-zinc-500 line-clamp-1">{p.direccion}</p>
                          </td>
                          <td className="px-3 py-2">
                            {p.realizada ? (
                              <Badge className="bg-emerald-600 text-white hover:bg-emerald-600">
                                <Check className="mr-1 h-3 w-3" /> Realizada
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-zinc-500">Pendiente</Badge>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="time"
                              className="h-8 w-[7.5rem]"
                              value={p.hora_visita || p.hora_estimada || ""}
                              onChange={(e) => actualizarHoraDetalle(p.id, e.target.value)}
                              disabled={p.realizada}
                            />
                          </td>
                          <td className="px-3 py-2">
                            {p.hora_recalculada ? (
                              <span className={`font-medium ${p.realizada ? "text-emerald-800" : "text-zinc-800"}`}>
                                {p.hora_recalculada}
                                {p.realizada ? (
                                  <span className="ml-1 text-[10px] font-normal text-emerald-700">real</span>
                                ) : (
                                  <span className="ml-1 text-[10px] font-normal text-zinc-500">est.</span>
                                )}
                              </span>
                            ) : (
                              <span className="text-zinc-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {desvio ? (
                              <span className={`text-xs font-semibold ${desvio.cls}`}>{desvio.txt}</span>
                            ) : (
                              <span className="text-zinc-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-zinc-600">{p.duracion_minutos || 30} min</td>
                          <td className="px-3 py-2 text-zinc-600">
                            {p.km_desde_anterior?.km != null
                              ? `${Number(p.km_desde_anterior.km).toFixed(1)} km`
                              : p.km_desde_anterior?.desde_origen
                                ? "Origen"
                                : p.orden === 1
                                  ? "—"
                                  : "Sin GPS"}
                            {p.km_desde_anterior?.minutos != null
                              ? ` · ${p.km_desde_anterior.minutos} min`
                              : ""}
                            {p.km_desde_anterior?.desde_origen && (
                              <span className="ml-1 text-[10px] text-zinc-400">
                                {p.km_desde_anterior.fuente === "primer_picado"
                                  ? "primer picado"
                                  : "centro"}
                              </span>
                            )}
                            {p.km_desde_anterior?.maps_url && (
                              <a
                                href={p.km_desde_anterior.maps_url}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-2 text-[11px] text-sky-600 hover:underline"
                              >
                                Maps
                              </a>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {paradasDetalle.some((p) => p.realizada) && (
                <p className="text-[11px] text-zinc-500">
                  Hora recálc.: la realizada usa la hora real; las siguientes suman duración de visita + desplazamiento.
                  Desvío en rojo = retraso respecto a la hora planificada; en azul = adelanto.
                </p>
              )}

              <RutaRoadmapVisual ruta={detalle} onOpenParada={() => {}} />
              {detalle.notas && <p className="text-sm text-zinc-600"><strong>Notas:</strong> {detalle.notas}</p>}
            </div>
          )}
        </>
      )}

      <Dialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) setForm(formVacioRuta()); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editando ? "Editar ruta comercial" : "Nueva ruta comercial"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Trabajador</Label>
              <Select
                value={form.usuario_id || "none"}
                onValueChange={(v) => setForm({ ...form, usuario_id: v === "none" ? "" : v })}
                disabled={editando}
              >
                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Seleccionar…</SelectItem>
                  {trabajadores.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>{u.nombre} {u.apellidos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha</Label>
                <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
              </div>
              <div>
                <Label>Título</Label>
                <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ruta zona norte…" />
              </div>
            </div>
            <div>
              <Label>Añadir cliente / parada</Label>
              <ClienteBusquedaSelect
                clientes={clientes}
                yaEnRuta={clientesEnRuta.conteo}
                onSelect={(c) => anadirParadaForm(String(c.id))}
              />
              {clientesEnRuta.ids.size > 0 && (
                <p className="mt-1 text-[11px] text-zinc-500">
                  Fondo azul suave: clientes ya añadidos a esta ruta (puedes volver a añadirlos si hace falta).
                </p>
              )}
            </div>

            {form.paradas.length > 0 && (
              <div className="space-y-3 rounded-xl border border-sky-100 bg-sky-50/70 p-3">
                {form.origen && (
                  <p className="rounded-md bg-violet-50/80 px-2 py-1 text-[11px] text-violet-800/80">
                    Origen: <strong>{form.origen.nombre || "Empresa"}</strong>
                    {form.origen.fuente === "primer_picado"
                      ? " · GPS del primer picado del día"
                      : " · centro de trabajo"}
                    {form.origen.direccion ? ` · ${form.origen.direccion}` : ""}
                    {!form.origen.geocodificado && form.origen.latitud == null ? " (sin geocodificar)" : ""}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Salida desde empresa</Label>
                    <Input
                      type="time"
                      className="h-8 border-sky-100 bg-white/90"
                      value={form.hora_inicio || "09:00"}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm((prev) => ({ ...prev, hora_inicio: v }));
                        if (recalcTimer.current) clearTimeout(recalcTimer.current);
                        recalcTimer.current = setTimeout(() => {
                          planificarHorarios(null, null, v);
                        }, 500);
                      }}
                    />
                    <p className="mt-0.5 text-[10px] text-zinc-500">Según horario del trabajador ese día</p>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 rounded-md bg-amber-50/80 px-2 py-1.5 text-xs text-amber-900/70">
                      <input
                        type="checkbox"
                        checked={!!form.conservar_primera}
                        onChange={(e) => setForm({ ...form, conservar_primera: e.target.checked })}
                      />
                      Mantener 1ª parada al optimizar
                    </label>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1 border-violet-200 bg-violet-100/70 text-violet-800 hover:bg-violet-100 hover:text-violet-900"
                    disabled={optimizando || form.paradas.length < 2 || !form.usuario_id}
                    onClick={optimizarRuta}
                  >
                    {optimizando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    Optimizar recorrido
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1 border-teal-200 bg-teal-100/70 text-teal-800 hover:bg-teal-100 hover:text-teal-900"
                    disabled={optimizando || !form.paradas.length || !form.usuario_id}
                    onClick={() => planificarHorarios()}
                  >
                    <Clock3 className="h-3.5 w-3.5" />
                    Recalcular horarios
                  </Button>
                </div>
                {resumenPlanning && (
                  <p className="rounded-md bg-emerald-50/90 px-2 py-1 text-[11px] text-emerald-800/80">
                    Recorrido ~ {Number(resumenPlanning.km_total || 0).toFixed(1)} km
                    {" · "}viaje {resumenPlanning.minutos_viaje_total || 0} min
                    {" · "}salida {form.hora_inicio || "—"}
                    {" · "}fin estimado {resumenPlanning.hora_fin_estimada || "—"}
                  </p>
                )}
                <ol className="space-y-2">
                  {form.paradas.map((p, i) => (
                    <li
                      key={p._key || `${p.cliente_id}-${i}`}
                      className={`rounded-lg border p-2.5 ${
                        i % 2 === 0
                          ? "border-rose-100 bg-rose-50/60"
                          : "border-sky-100 bg-sky-50/80"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex flex-col gap-0.5">
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={i === 0} onClick={() => moverParada(i, -1)}>
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={i === form.paradas.length - 1} onClick={() => moverParada(i, 1)}>
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium">{String(i + 1).padStart(2, "0")}. {p.nombre}</p>
                              <p className="text-xs text-zinc-500 line-clamp-1">{p.direccion || "Sin dirección"}</p>
                              {(i === 0 || p.minutos_desplazamiento || p.km_desde_anterior) && (
                                <p className="text-[11px] font-medium text-teal-700/80">
                                  {i === 0
                                    ? `Desde ${form.origen?.nombre || "empresa"} · `
                                    : ""}
                                  {p.km_desde_anterior?.km != null
                                    ? `${Number(p.km_desde_anterior.km).toFixed(1)} km`
                                    : "—"}
                                  {p.minutos_desplazamiento
                                    ? ` · ${p.minutos_desplazamiento} min desplazamiento`
                                    : ""}
                                </p>
                              )}
                            </div>
                            <Button type="button" variant="ghost" size="icon" className="text-rose-600" onClick={() => quitarParada(p._key)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px] text-zinc-500">Hora visita</Label>
                              <Input
                                type="time"
                                className="h-8"
                                value={p.hora_estimada || ""}
                                onChange={(e) => onCambiarHoraVisita(p._key, e.target.value)}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] text-zinc-500">Min. en visita</Label>
                              <Input
                                type="number"
                                min={5}
                                max={480}
                                step={5}
                                className="h-8"
                                value={p.duracion_minutos ?? 30}
                                onChange={(e) => onCambiarDuracion(p._key, Number(e.target.value) || 30)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div>
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
            </div>
            <p className="text-xs text-zinc-500">
              El recorrido parte de la empresa a la hora de jornada del trabajador. La 1ª visita suma el desplazamiento;
              si cambias una hora o duración, se recalculan las siguientes.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
            <Button disabled={guardando} onClick={guardarRuta}>
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : (editando ? "Guardar cambios" : "Crear y notificar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={duplicarOpen} onOpenChange={setDuplicarOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Duplicar ruta a otra fecha</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-600">
              Se copian las paradas y horas de visita (sin anotaciones, gastos ni adjuntos) para poder editarlas después.
            </p>
            <div>
              <Label>Nueva fecha</Label>
              <Input type="date" value={duplicarForm.fecha} onChange={(e) => setDuplicarForm({ ...duplicarForm, fecha: e.target.value })} />
            </div>
            <div>
              <Label>Trabajador</Label>
              <Select
                value={duplicarForm.usuario_id || "none"}
                onValueChange={(v) => setDuplicarForm({ ...duplicarForm, usuario_id: v === "none" ? "" : v })}
              >
                <SelectTrigger><SelectValue placeholder="Mismo trabajador" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Mismo / seleccionar…</SelectItem>
                  {trabajadores.map((u) => (
                    <SelectItem key={u.id} value={String(u.id)}>{u.nombre} {u.apellidos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Título (opcional)</Label>
              <Input value={duplicarForm.titulo} onChange={(e) => setDuplicarForm({ ...duplicarForm, titulo: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicarOpen(false)}>Cancelar</Button>
            <Button disabled={guardando} onClick={duplicarRuta}>
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Duplicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!clienteForm} onOpenChange={(v) => !v && setClienteForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{clienteForm?.id ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
          </DialogHeader>
          {clienteForm && (
            <div className="space-y-3">
              {[["nombre", "Nombre *"], ["codigo", "Código"], ["nif", "NIF"], ["telefono", "Teléfono"], ["email", "Email"]].map(([k, lab]) => (
                <div key={k}>
                  <Label>{lab}</Label>
                  <Input value={clienteForm[k] || ""} onChange={(e) => setClienteForm({ ...clienteForm, [k]: e.target.value })} />
                </div>
              ))}
              <div>
                <Label>Dirección</Label>
                <Input value={clienteForm.direccion || ""} onChange={(e) => setClienteForm({ ...clienteForm, direccion: e.target.value })} />
              </div>
              <DireccionEspanaFields
                value={clienteForm}
                onChange={(next) => setClienteForm({
                  ...clienteForm,
                  codigo_postal: next.codigo_postal || "",
                  poblacion: next.poblacion || "",
                  provincia: next.provincia || "",
                })}
              />
              <MapaSelectorPunto
                lat={clienteForm.latitud}
                lng={clienteForm.longitud}
                onChange={(lat, lng) => setClienteForm({ ...clienteForm, latitud: lat, longitud: lng })}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="gap-1"
                  onClick={async () => {
                    try {
                      const fd = new FormData();
                      fd.append("direccion", [clienteForm.direccion, clienteForm.codigo_postal, clienteForm.poblacion, clienteForm.provincia].filter(Boolean).join(", "));
                      const { data } = await api.post("/rutas-comerciales/geocodificar", fd);
                      setClienteForm({
                        ...clienteForm,
                        latitud: data.lat,
                        longitud: data.lng,
                        direccion: clienteForm.direccion || data.direccion,
                      });
                      toast.success("Ubicación localizada");
                    } catch (e) {
                      toast.error(formatApiError(e));
                    }
                  }}
                >
                  <MapPinned className="h-4 w-4" /> Geocodificar
                </Button>
                {clienteForm.latitud != null && (
                  <Button asChild variant="outline">
                    <a
                      href={`https://www.google.com/maps?q=${clienteForm.latitud},${clienteForm.longitud}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Google Maps
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setClienteForm(null)}>Cancelar</Button>
            <Button disabled={guardando} onClick={guardarCliente}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
